import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Bell, BellOff, Check, CheckCheck, Trash2, ChevronLeft, 
  ChevronRight, Info, MessageSquare, Star, AlertCircle, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Notification type icons
const getNotificationIcon = (type) => {
  switch (type) {
    case 'advisor_response':
      return <MessageSquare className="w-5 h-5 text-[var(--gold)]" />;
    case 'client_feedback':
      return <Star className="w-5 h-5 text-amber-500" />;
    case 'board_shared':
      return <Info className="w-5 h-5 text-green-500" />;
    case 'material_added':
      return <Check className="w-5 h-5 text-purple-500" />;
    default:
      return <Bell className="w-5 h-5 text-stone-500" />;
  }
};

// Format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const NotificationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all'); // all, unread
  const limit = 20;

  const fetchNotifications = async () => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString(),
        unread_only: (filter === 'unread').toString()
      });
      
      const response = await axios.get(`${API}/notifications?${params}`, { withCredentials: true });
      setNotifications(response.data.notifications);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, page, filter]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {
        notification_ids: [notificationId]
      }, { withCredentials: true });
      
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { withCredentials: true });
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
      setTotal(prev => prev - 1);
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const clearAll = async () => {
    if (!confirm('Are you sure you want to clear all notifications?')) return;
    
    try {
      await axios.delete(`${API}/notifications`, { withCredentials: true });
      setNotifications([]);
      setTotal(0);
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <Bell className="w-16 h-16 mx-auto text-stone-300 mb-4" />
            <h1 className="text-2xl font-medium text-stone-900 mb-2">Sign in to view notifications</h1>
            <p className="text-stone-500 mb-6">Keep track of advisor responses, client feedback, and more.</p>
            <Button onClick={() => navigate('/login')} className="bg-stone-900 hover:bg-stone-800 text-white">
              Sign In
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pt-8">
            <div>
              <h1 className="text-3xl font-medium text-stone-900" data-testid="notifications-title">Notifications</h1>
              <p className="text-stone-500 mt-1">
                {total} notification{total !== 1 ? 's' : ''} {unreadCount > 0 && `(${unreadCount} unread)`}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-sm"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearAll}
                  className="text-sm text-red-600 hover:text-red-700 hover:border-red-300"
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Clear all
                </Button>
              )}
            </div>
          </div>
          
          {/* Filter */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFilter('all'); setPage(0); }}
              className={filter === 'all' ? 'bg-stone-900' : ''}
            >
              All
            </Button>
            <Button
              variant={filter === 'unread' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFilter('unread'); setPage(0); }}
              className={filter === 'unread' ? 'bg-stone-900' : ''}
            >
              Unread
            </Button>
          </div>
          
          {/* Notifications List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-stone-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="bg-white border border-stone-200 p-12 text-center">
              <BellOff className="w-12 h-12 mx-auto text-stone-300 mb-4" />
              <h2 className="text-xl font-medium text-stone-900 mb-2">No notifications</h2>
              <p className="text-stone-500">
                {filter === 'unread' ? "You're all caught up!" : "You don't have any notifications yet."}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-stone-200 divide-y divide-stone-100">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`p-4 hover:bg-stone-50 transition-colors cursor-pointer ${
                    !notification.is_read ? 'bg-[var(--gold)]/5' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`text-sm font-medium ${!notification.is_read ? 'text-stone-900' : 'text-stone-700'}`}>
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-[var(--gold)] rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-sm text-stone-600 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-stone-400 mt-1">
                        {formatRelativeTime(notification.created_at)}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex-shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {!notification.is_read && (
                        <button
                          onClick={() => markAsRead(notification.notification_id)}
                          className="p-2 hover:bg-stone-100 rounded transition-colors"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4 text-stone-400" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.notification_id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-stone-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-stone-500">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default NotificationsPage;
